/**
 * O acervo compartilhado — as quatro abas e o download por URL assinada.
 *
 * A categoria NÃO é escolhida na tela: ela deriva da entidade de origem do anexo, no
 * materializador (`pedido_venda` → PI · `campanha` → Relatório · `nf` → NF). A tela só
 * agrupa o que recebe; se ela classificasse, haveria duas classificações do mesmo
 * arquivo, e um dia elas discordariam.
 *
 * Em "Todas", o mesmo arquivo compartilhado com duas empresas do portal chega UMA vez (a API
 * deduplica pelo anexo, DS-527), com as empresas que o recebem — e a linha diz quais são.
 */
import { useState } from 'react'
import { BarChart3, Download, File, FileBadge, FileText, FolderOpen } from 'lucide-react'
import { urlDeAnexo, type DocumentoDoPortal } from '@dv/contracts'
import { pedir } from '../api/cliente.js'
import { FalhaDoPortal, mensagemDe } from '../api/erros.js'
import { Vazio } from '../componentes/estados.js'
import { useAviso } from '../componentes/toast.js'
import { useEmpresas } from '../estado/empresas.js'
import { dataBr, tamanhoBr } from '../formato.js'

/** As abas do protótipo 11.2:160-163 — o rótulo no plural, a categoria como o materializador a grava. */
const ABAS = [
  { rotulo: 'Todos', categoria: null },
  { rotulo: 'PIs', categoria: 'PI' },
  { rotulo: 'Relatórios', categoria: 'Relatório' },
  { rotulo: 'NFs', categoria: 'NF' },
] as const
type Aba = (typeof ABAS)[number]['rotulo']

/** Um ícone por categoria, como a linha de documento do protótipo (PK-09). */
function IconeDaCategoria({ categoria }: { categoria: string }) {
  const Icone = categoria === 'PI' ? FileText : categoria === 'NF' ? FileBadge : categoria === 'Relatório' ? BarChart3 : File
  return <Icone size={20} className="icone-documento" aria-hidden="true" />
}

export function Documentos({
  itens,
  comAbas,
  titulo,
}: {
  itens: readonly DocumentoDoPortal[]
  /** O parceiro (11.1) tem lista simples; o cliente (11.2) tem as quatro abas. */
  comAbas: boolean
  /** "Documentos" no 11.2 e "Documentos compartilhados" no 11.1, como nos protótipos. */
  titulo: string
}) {
  const [aba, setAba] = useState<Aba>('Todos')
  const [baixando, setBaixando] = useState<string | null>(null)
  const [erro, setErro] = useState('')
  const avisar = useAviso()
  const empresas = useEmpresas()

  const categoria = ABAS.find((item) => item.rotulo === aba)?.categoria ?? null
  const visiveis = comAbas && categoria !== null ? itens.filter((item) => item.categoria === categoria) : itens

  async function baixar(item: DocumentoDoPortal): Promise<void> {
    if (baixando !== null) return
    setErro('')
    setBaixando(item.id)
    try {
      // Um clique, um link novo. O token curto não fica em estado, cache ou storage.
      const link = await pedir(`documentos/${item.id}/url`, urlDeAnexo)
      const ancora = document.createElement('a')
      ancora.href = link.url
      ancora.download = item.nome
      ancora.rel = 'noopener'
      document.body.appendChild(ancora)
      ancora.click()
      ancora.remove()
      /*
        O aviso é a única prova de que o clique fez alguma coisa.

        O botão volta de "Preparando…" para "Baixar" e o arquivo desce numa barra do
        navegador que pode estar recolhida. Sem esta linha, quem clica e não vê nada
        clica de novo — e cada clique queima uma URL assinada nova.
      */
      avisar({ tom: 'sucesso', titulo: 'Baixando documento', texto: item.nome })
    } catch (falha) {
      setErro(falha instanceof FalhaDoPortal ? mensagemDe(falha.tipo) : 'Não foi possível baixar o documento.')
    } finally {
      setBaixando(null)
    }
  }

  return (
    <section className="painel documentos" data-teste="documentos">
      <div className="titulo">
        <h2>{titulo}</h2>
        {comAbas && (
          <div className="abas" role="tablist" aria-label="Categorias de documento">
            {ABAS.map((item) => (
              <button
                key={item.rotulo}
                type="button"
                role="tab"
                aria-selected={aba === item.rotulo}
                onClick={() => {
                  setAba(item.rotulo)
                }}
              >
                {item.rotulo}
              </button>
            ))}
          </div>
        )}
      </div>
      {erro !== '' && (
        <div className="erro erro-download" role="alert">
          {erro}
        </div>
      )}
      {visiveis.length === 0 ? (
        <Vazio
          icone={<FolderOpen size={20} />}
          // Com o acervo zerado, "nesta categoria" sugeriria arquivos nas outras abas (P43 · DS-531).
          titulo={itens.length === 0 || !(comAbas && aba !== 'Todos') ? 'Nenhum documento ainda' : 'Nenhum documento nesta categoria'}
          texto="Arquivos compartilhados pela DigitalView aparecem aqui."
        />
      ) : (
        <ul className="lista-documentos">
          {visiveis.map((item) => (
            <li key={item.id}>
              {/*
                A linha inteira é o botão, como no protótipo (11.2:166): ícone da categoria,
                nome, "situação data · tamanho" e o ícone de baixar. O nome acessível diz a
                ação e o arquivo.
              */}
              <button
                type="button"
                className="linha-documento"
                disabled={baixando !== null}
                aria-busy={baixando === item.id}
                aria-label={`Baixar ${item.nome}`}
                onClick={() => {
                  void baixar(item)
                }}
              >
                <IconeDaCategoria categoria={item.categoria} />
                <span>
                  <strong>{item.nome}</strong>
                  <small>
                    {item.meta ?? item.categoria} {dataBr(item.dataDocumento)} · {tamanhoBr(item.tamanhoBytes)}
                    {empresas.mostrar && (
                      <span className="empresas-do-documento" data-teste="empresas-do-documento">
                        {' · '}
                        {empresas.nomesDe(item.empresaIds)}
                      </span>
                    )}
                  </small>
                </span>
                {baixando === item.id ? <small>Preparando…</small> : <Download size={16} className="icone-baixar" aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
